import { DtoEmotion, DtoVendor } from '@/shared/constants';
import { ContentVariant } from '@/features/remotion-editor/editor/assets/assets';
import { ReframeMeta } from '@/shared/apis/editor/reframe-types';

export const CLIP_TYPES = {
  AUDIO: 'audio',
  CAPTION: 'caption',
  TITLE: 'title',
  VIDEO: 'video',
  IMAGE: 'image',
  GIF: 'gif',
  WEBP: 'webp',
} as const;
export type ClipType = (typeof CLIP_TYPES)[keyof typeof CLIP_TYPES];

export interface AlignWord {
  word: string;
  start?: number;
  end?: number;
  /**
   * Soniox 화자 분리 활성 시 채워지는 화자 라벨 (예: ["1"]).
   * WhisperX fallback 또는 화자 분리 미사용 경로에서는 undefined.
   */
  speaker?: string[];
}

export interface BaseClipInfo {
  id?: number;
  clipType: ClipType;
  videoStart?: number; // 클립의 시작 시간 (스토리 내에서의 시작 시간)
  videoEnd?: number; // 클립의 끝 시간 (스토리 내에서의 끝 시간)
  linkId?: number;
  sceneId?: number;
}

export interface ContentClipInfo extends BaseClipInfo {
  contentId?: number;
  // v1.0 호환: content_uri, v2.0: content_public_uri
  contentUri?: string;
  contentPublicUri?: string;
  // 다중 해상도 변환 영상 (프리뷰 전용, 렌더링 시 미사용)
  variants?: ContentVariant[];
  start?: number; // 클립의 시작 시간 (원본 비디오에서의 시작 시간)
  end?: number; // 클립의 끝 시간 (원본 비디오에서의 끝 시간)
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  sourceVideoWidth?: number;
  sourceVideoHeight?: number;
  sourceAudioMuted?: boolean;
  /**
   * 영상 내부 오디오가 타임라인의 단일 재생 권위인지 나타낸다.
   * 립싱크 영상은 별도 audio clip 없이 이 값을 사용한다.
   */
  audioPlaybackMode?: string | null;
}

/**
 * 번역 자막 cue 1개. `start`/`end` 는 `alignWords` 와 같은 기준(캡션 시작 0-based 상대 ms)이고
 * 슬롯 안에서 오름차순·비중첩이다. 화면에는 cue 1개가 자막 한 장으로 뜬다.
 */
export interface CaptionTranslationCue {
  text: string;
  start: number;
  end: number;
}

/**
 * 번역 결과 상태.
 * - `translated`: 타겟 언어로 번역됨
 * - `passthrough`: 원문이 이미 타겟 언어라 표기만 정리됨 (표시 대상)
 * - `failed`: 번역 실패 — 원문을 그대로 쓴다
 */
export type CaptionTranslationStatus = 'translated' | 'passthrough' | 'failed';

/**
 * 캡션 클립에 붙는 번역 결과. 원문 필드(`segment`/`alignWords`)는 건드리지 않는다.
 * "키가 있으면 번역됨" 이 아니라 `status` 로 판정한다.
 */
export interface CaptionTranslation {
  targetLanguage: string;
  status: CaptionTranslationStatus;
  cues: CaptionTranslationCue[];
}

export interface CaptionClipInfo extends BaseClipInfo {
  clipType: typeof CLIP_TYPES.CAPTION;
  segment: string;
  alignWords: AlignWord[];
  /** LTS 자막 번역 결과. 번역을 켜지 않은 프로젝트에서는 부재(undefined/null). */
  translation?: CaptionTranslation | null;
  highlightColor?: string | null;
  highlightEffect?: 'instant' | 'karaoke-fill' | 'karaoke-line-sweep';
  captionPresentationLeadMs?: number | null;
  captionTimingClass?: string | null;
  captionAlignmentMode?: string | null;
  captionTimingFallbackUsed?: boolean | null;
  captionTimingPolicyVersion?: number | null;
  captionStyle?: Record<string, unknown> | null;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

export interface TitleClipInfo extends BaseClipInfo {
  clipType: typeof CLIP_TYPES.TITLE;
  title: string;
  selected?: boolean;
}

export interface AudioClipInfo extends ContentClipInfo {
  clipType: typeof CLIP_TYPES.AUDIO;
  vendorModel: DtoVendor;
  voiceId: string | null;
  pitch: number | null;
  emotion: DtoEmotion | null;
  speedFactor: number | null;
  requestVoiceId?: string | null;
}

export interface VideoClipInfo extends Omit<ContentClipInfo, 'sceneId'> {
  clipType: typeof CLIP_TYPES.VIDEO;
  reframeMeta?: ReframeMeta;
  // 투명(알파) 비디오: 아바타 오버레이처럼 배경이 크로마키된 클립.
  // hasAlpha=true 면 alphaVideoUrl(투명 WebM)을 에디터 비디오 소스로 사용한다.
  hasAlpha?: boolean;
  alphaVideoUrl?: string | null;
  // 생성 역할 계약. hasAlpha는 렌더 신호이므로 아바타 세그먼트 식별에 사용하지 않는다.
  sourceRole?: string | null;
  // defer-baking(v2) 세그먼트 아바타 클립: 클립이 아이템보다 짧으면 반복 재생.
  loop?: boolean | null;
}

export interface ImageClipInfo extends Omit<ContentClipInfo, 'sceneId'> {
  clipType: typeof CLIP_TYPES.IMAGE;
  videoStart?: number; // 클립의 시작 시간 (스토리 내에서의 시작 시간)
  videoEnd?: number; // 클립의 끝 시간 (스토리 내에서의 끝 시간)
}

// export interface ClipEditorInfoNew {
//   createdAt: number;
//   updatedAt: number;
//   id: number;
//   type?: number;
//   storyClipInfoId: number;
//   payload: {
//     overlays: Overlay[];
//     savedAt?: number;
//     aspectRatio?: DtoAspectRatio;
//   };
// }

export type ClipInfo =
  | AudioClipInfo
  | CaptionClipInfo
  | TitleClipInfo
  | VideoClipInfo
  | ContentClipInfo;

export const isCaptionClip = (clip: ClipInfo): clip is CaptionClipInfo => {
  return clip.clipType === CLIP_TYPES.CAPTION && 'alignWords' in clip;
};

export const isAudioClip = (clip: ClipInfo): clip is AudioClipInfo => {
  return clip.clipType === CLIP_TYPES.AUDIO;
};

export const isTitleClip = (clip: ClipInfo): clip is TitleClipInfo => {
  return clip.clipType === CLIP_TYPES.TITLE && 'title' in clip;
};

export const isImageClip = (clip: ClipInfo): clip is ImageClipInfo => {
  return clip.clipType === CLIP_TYPES.IMAGE;
};

export const isTitleClipEditorData = (item: ClipInfo[]): TitleClipInfo[] => {
  if (!item) return [];

  return item.filter((clip) => isTitleClip(clip));
};

export const isVideoClip = (clip: ClipInfo): clip is VideoClipInfo => {
  return clip.clipType === CLIP_TYPES.VIDEO;
};

export const contentClipTypeGuard = (
  clip: ClipInfo,
): clip is ContentClipInfo => {
  return 'contentId' in clip;
};
